import { useEffect, useState } from 'react'
import { fetchWpsProjectPipeSets } from '../api/data360'
import { useProjects } from '../contexts/ProjectsContext'
import type { PipeSizeSet, ProjectDetails } from '../types'

const EMPTY_PIPE_SETS: PipeSizeSet[] = []

function pipeSetsSyncedEqual(a: PipeSizeSet[], b: PipeSizeSet[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i]
    const right = b[i]
    if (
      left.id !== right.id ||
      left.data360PipeId !== right.data360PipeId ||
      left.data360ProjectCategory !== right.data360ProjectCategory ||
      left.data360ProjectSubType !== right.data360ProjectSubType ||
      left.nominalOd !== right.nominalOd ||
      left.wallThickness !== right.wallThickness
    ) {
      return false
    }
  }
  return true
}

export function useData360ProjectPipeSets(project: ProjectDetails | undefined): {
  pipeSets: PipeSizeSet[]
  pipesLoading: boolean
} {
  const { patchProjectPipeSets } = useProjects()
  const [pipeSets, setPipeSets] = useState<PipeSizeSet[]>(project?.pipeSets ?? EMPTY_PIPE_SETS)
  const [pipesLoading, setPipesLoading] = useState(false)

  const projectId = project?.id
  const data360ProjectId = project?.data360ProjectId

  useEffect(() => {
    if (!projectId) {
      setPipeSets(EMPTY_PIPE_SETS)
      return
    }

    if (!data360ProjectId) {
      setPipeSets(project?.pipeSets ?? EMPTY_PIPE_SETS)
      return
    }

    const cachedPipeSets = project?.pipeSets ?? EMPTY_PIPE_SETS
    let cancelled = false

    ;(async () => {
      try {
        if (cachedPipeSets.length > 0) {
          setPipeSets(cachedPipeSets)
        }
        setPipesLoading(true)
        const synced = await fetchWpsProjectPipeSets(projectId)
        if (cancelled) return
        setPipeSets(synced)
        if (!pipeSetsSyncedEqual(cachedPipeSets, synced)) {
          patchProjectPipeSets(projectId, synced)
        }
      } catch {
        if (!cancelled) setPipeSets(cachedPipeSets)
      } finally {
        if (!cancelled) setPipesLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // Intentionally stable: do not depend on `project` — patchProjectPipeSets updates
    // project reference and would re-trigger an infinite fetch loop.
  }, [projectId, data360ProjectId, patchProjectPipeSets])

  return { pipeSets, pipesLoading }
}
